// src/pages/GiftSetDetail.jsx
import React, { useEffect, useState, useLayoutEffect } from "react";
import { useRef } from "react";
import { useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import PerfumeDetail from "./PerfumeDetail"; // <- გამოვიყენებთ მოდალში
// 🔗 იმპორტები მოდალებისთვის
import PriceChangedModal from "./PriceChangeModal";
import AuthExpiredModal from "./AuthExpiredModal";
import AddressPopup from "./AddressPopup";
import MiniRegisterModal from "./MiniRegisterModal";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import { api } from "../lib/http";

// ✨ ორი საბაზო URL: gift set detail/get_images და გადახდების ენდპოინტებისთვის
const GS_BASE_URL = "https://shopaholics.ge/api/gift_sets";
const API_BASE_URL = "https://shopaholics.ge/api";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

const spin = keyframes`0%{transform:rotate(0)}100%{transform:rotate(360deg)}`;
const Loader = styled.div`min-height: 60vh; display: grid; place-items: center;`;
const Spinner = styled.div`
  width: 42px; height: 42px;
  border: 4px solid rgba(0,0,0,.08);
  border-top-color: #000;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;
const Page = styled.div`max-width: 1280px; margin: 16px auto 32px; padding: 0 16px;`;
const Grid = styled.div`
  display: grid; grid-template-columns: 1.1fr 1fr; gap: 16px; align-items: start;
  @media (max-width: 1024px){ grid-template-columns: 1fr; }
`;
const Card = styled.div`
  background: ${COLORS.card}; border: 1px solid ${COLORS.line};
  border-radius: 16px; box-shadow: ${COLORS.shadow};
`;
const TitleDesktop = styled.h1`
  font-size: 22px; font-weight: 900; color: ${COLORS.text}; margin: 8px 0 16px;
  @media (max-width: 768px){ display:none; }
`;
const TitleMobile = styled.h1`
  display:none; font-size: 20px; font-weight: 900; color: ${COLORS.text}; margin: 8px 0 12px;
  @media (max-width: 768px){ display:block; }
`;

const GalleryCard = styled(Card)`
  padding: 16px; display: grid; gap: 12px; align-self: start; height: max-content;
`;
const MainImageWrap = styled.div`
  background: ${COLORS.subtle}; border: 1px solid ${COLORS.line};
  border-radius: 14px; width: 100%; aspect-ratio: 1 / 1;
  display: grid; justify-items: center; align-items: start; padding-top: 10px; overflow: hidden;
`;
const MainImage = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;     /* პროპორციულად ჩასვავს ჩარჩოში */
  object-position: center; /* სურვილისამებრ: ცენტრში გაასწორებს */
  display: block;          /* inline-gap აღარ იქნება */
`;
const Thumbs = styled.div`
  display: flex; gap: 10px; overflow-x: auto; padding-bottom: 6px;
  scrollbar-width: thin;
  &::-webkit-scrollbar{ height:6px; }
  &::-webkit-scrollbar-thumb{ background:#c8c8cc; border-radius:10px; }
  img{
    width: 56px; height: 56px; object-fit: cover; border-radius: 10px; border: 2px solid transparent; cursor: pointer; flex: 0 0 auto;
  }
  img.selected, img:hover{ border-color: #000; }
`;

const DetailsCard = styled(Card)`padding: 16px 16px 8px;`;
const PriceRow = styled.div`
  display:flex; align-items:center; gap:8px; font-weight:800; margin: 6px 0 12px;
  .old{ text-decoration: line-through; color:#9ca3af; }
`;
const Row = styled.div`display:flex; gap:6px; color:${COLORS.text}; margin: 6px 0; strong{ font-weight:900; }`;
const Info = styled.p`color:${COLORS.text}; margin: 6px 0; line-height: 1.5; white-space: pre-wrap;`;
const Toggle = styled.button`
  width:100%; display:flex; align-items:center; justify-content:space-between;
  padding: 12px 14px; margin-top: 10px;
  background: #f9f9f9; border: 1px solid ${COLORS.line};
  border-radius: 12px; cursor:pointer; font-weight: 900; color:${COLORS.text};
`;
const ToggleContent = styled.div`
  overflow: hidden; max-height: ${({open}) => open ? "600px" : "0px"};
  transition: max-height .35s ease; padding: ${({open}) => open ? "10px 2px 2px" : "0 2px"};
  p{ color:${COLORS.dim}; white-space: pre-wrap; }
`;

const ItemsCard = styled(Card)`margin-top: 16px; padding: 12px;`;
const ItemRow = styled.div`
  display:grid; grid-template-columns: 64px 1fr auto; gap: 10px; align-items:center;
  border-bottom: 1px solid #f1f1f3; padding: 8px 0;
  &:last-child{ border-bottom: none; }
  @media (max-width: 640px){ grid-template-columns: 1fr; }
`;
const Thumb = styled.div`
  width:64px; height:64px; background:${COLORS.subtle}; border:1px solid ${COLORS.line}; border-radius:10px; display:grid; place-items:center; overflow:hidden;
  img{ width: 100%; height: 100%; object-fit: cover; }
`;
const QuickBtn = styled.button`
  height: 36px; padding: 0 10px; border-radius: 10px; cursor:pointer;
  border: 1px solid ${COLORS.line}; background:#fff; font-weight:800;
`;

const ActionsCard = styled(Card)`
  margin-top: 16px; padding: 16px;
  display: grid; gap: 12px; position: static; top: 12px;
  @media (max-width: 1024px){ position: static; }
`;
const QtyRow = styled.div`display:flex; align-items:center; gap: 10px;`;
const QtyBadge = styled.div`min-width: 80px; color:${COLORS.text};`;
const QtyField = styled.div`display:flex; align-items:center; gap: 6px;`;
const QtyBtn = styled.button`
  width: 34px; height: 34px; border-radius: 999px;
  border: 1px solid ${COLORS.line}; background: #fff;
  display:grid; place-items:center; font-size:18px; cursor:pointer;
  transition: transform .15s ease;
  &:hover{ transform: translateY(-1px); }
  &:disabled{ opacity:.4; cursor:not-allowed; }
`;
const QtyInput = styled.input`
  width: 42px; height: 34px; border:1px solid ${COLORS.line};
  border-radius: 8px; text-align:center; font-size: 14px; padding: 0 4px;
  appearance: textfield;
  &::-webkit-outer-spin-button,&::-webkit-inner-spin-button{ -webkit-appearance: none; margin:0; }
`;
const Total = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  padding: 10px 12px; border:1px solid ${COLORS.line}; border-radius: 12px; background:#fff;
  strong{ font-size: 18px; }
`;
const Buttons = styled.div`
  display:grid; grid-template-columns: 1fr; gap: 10px;
  .btn{ height: 44px; border-radius: 12px; border:1px solid ${COLORS.line}; font-weight: 900; cursor:pointer; transition: transform .12s ease; }
  .btn:active{ transform: scale(.99); }
  .primary{ background: ${COLORS.text}; color:#fff; }
  .secondary{ background: #fff; color:${COLORS.text}; }
  .or{ text-align:center; color:${COLORS.dim}; font-weight:700; }
`;

// Modal shell to host PerfumeDetail inside
const ModalBg = styled.div`
  position: fixed; inset:0; background: rgba(0,0,0,.45);
  display:grid; place-items:center; z-index: 3001;
`;
const ModalCard = styled.div`
  width: min(1200px, 96vw); max-height: 94vh;
  background:#fff; border-radius: 16px;
  border:1px solid ${COLORS.line}; box-shadow:${COLORS.shadow};
  padding: 8px 8px 12px;
  position: relative;
  overflow: hidden;
`;
const CloseBtn = styled.button`
  position: absolute;
  top: 10px; right: 10px;
  height: 36px; width: 36px;
  border-radius: 999px;
  border: 1px solid ${COLORS.line};
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(6px);
  display: grid; place-items: center;
  font-size: 18px; font-weight: 900;
  cursor: pointer;
  box-shadow: ${COLORS.shadow};
  transition: transform .15s ease, filter .15s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.98); }
  &:active { transform: scale(.98); }
  z-index: 3002;
`;
const ModalScroll = styled.div`
  max-height: calc(94vh - 0px);
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,.28) transparent;
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-track { background: rgba(0,0,0,.06); border-radius: 999px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,.28); border-radius: 999px; border: 2px solid transparent; background-clip: padding-box; }
  -webkit-overflow-scrolling: touch;
`;

// --- Simple stock modal (same vibe as CartOverlay) ---
const StockModalBg = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  z-index: 4000; display: grid; place-items: center;
`;
const StockModalCard = styled.div`
  width: min(520px, 92vw); background: #fff; border: 1px solid rgba(0,0,0,.08);
  border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,.08); padding: 18px; text-align: center;
`;

const PayOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 999999;
  display: grid;
  place-items: center;
  padding: 12px;
`;

const PayShell = styled.div`
  width: min(980px, 96vw);
  height: min(92vh, 860px);
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(0,0,0,.08);
  box-shadow: 0 18px 50px rgba(0,0,0,.18);
  overflow: hidden;

  display: flex;
  flex-direction: column;

  /* iOS safe-area */
  padding-bottom: env(safe-area-inset-bottom);
`;

const PayHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #fff;
`;

const PayTitle = styled.div`
  font-weight: 900;
  color: #111827;
  letter-spacing: .01em;
`;

const PayClose = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,.08);
  background: #111827;
  color: #fff;
  font-size: 22px;
  font-weight: 900;
  cursor: pointer;
  transition: transform .12s ease, filter .12s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.95); }
`;

const PayHint = styled.div`
  flex: 0 0 auto;
  padding: 10px 14px;
  font-size: 12.5px;
  color: #6b7280;
  font-weight: 800;
  border-bottom: 1px solid rgba(0,0,0,.06);
  background: #fafafa;
`;

const PayFrameWrap = styled.div`
  flex: 1 1 auto;
  min-height: 0; /* 🔥 ეს არის მთავარი რომ iframe არ მოიჭრას flex-ში */
  background: #fff;
`;

const PayFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;


function StockModal({ message, onClose }) {
  return (
    <StockModalBg onClick={onClose}>
      <StockModalCard onClick={(e)=>e.stopPropagation()}>
        <h3 style={{margin:"4px 0 8px", fontWeight:900}}>⚠️</h3>
        <p style={{margin:"0 0 12px", color:"#6b7280"}}>{message}</p>
        <button
          onClick={onClose}
          style={{
            height:40,padding:"0 16px",borderRadius:10,border:"1px solid rgba(0,0,0,.08)",
            background:"#111827",color:"#fff",fontWeight:800,cursor:"pointer"
          }}
        >
          OK
        </button>
      </StockModalCard>
    </StockModalBg>
  );
}
function PaymentIframeModal({ url, onClose, language }) {
  const T = (ka, en) => (language === "ka" ? ka : en);

  return (
    <PayOverlay onClick={onClose}>
      <PayShell onClick={(e) => e.stopPropagation()}>
        {/* Header (fixed) */}
        <PayHeader>
          <PayTitle>{T("გადახდის დადასტურება", "Confirm payment")}</PayTitle>
          <PayClose onClick={onClose} aria-label="Close">×</PayClose>
        </PayHeader>

        {/* Optional hint row (small) */}
        <PayHint>
          {T(
            "შეიყვანეთ SMS კოდი და დაასრულეთ გადახდა. თუ არაფერი ჩანს, დააჭირეთ Close და გავხსნით ჩვეულებრივად.",
            "Enter the SMS code to finish. If it’s blank, close and we’ll open it normally."
          )}
        </PayHint>

        {/* Iframe fills remaining height */}
        <PayFrameWrap>
          <PayFrame
            title="payment"
            src={url}
            allow="payment *; clipboard-read *; clipboard-write *"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </PayFrameWrap>
      </PayShell>
    </PayOverlay>
  );
}



export default function GiftSetDetail({ addToCart }) {
    const { version } = useDataRefresh();
  const { gift_set_id } = useParams();
  const { t, language } = useLanguage();

  const [gs, setGs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [openQuick, setOpenQuick] = useState(null);
  const [showNote, setShowNote] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [qty, setQty] = useState(1);

const [silentRefreshing, setSilentRefreshing] = useState(false); // ჩუმი refresh-ისთვის (UI-სთვის უხმოდ)
const firstLoadRef = useRef(true);
const [stockModalOpen, setStockModalOpen] = useState(false);
const [stockMessage, setStockMessage] = useState("");
const T = (ka, en) => (language === "ka" ? ka : en);

  const [payModalOpen, setPayModalOpen] = useState(false);
const [payUrl, setPayUrl] = useState("");


const openStockModal = (msg) => {
  setShowAddressPopup(false);    // ← ფასის/სტოკის პრობლემაზე დახურე მისამართის პოპაპი
  setStockMessage(msg);
  setStockModalOpen(true);
};

const [settings, setSettings] = useState(null);

  // 🔔 მოდალები/აუთენტიკაცია — იგივე, რაც PerfumeDetail-ში
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [popupAssumeHasPhone, setPopupAssumeHasPhone] = useState(false);
  const imgCacheRef = useRef(new Map()); // product_id -> thumb_url
  const hydrateThumbs = (data) => {
    if (!data || !Array.isArray(data.items)) return data;
    const cached = imgCacheRef.current;
    // seed cache from previous gs (თუ იყო)
    if (gs && Array.isArray(gs.items)) {
      for (const it of gs.items) {
        if (it?.product_id != null && it.thumb_url) {
          cached.set(String(it.product_id), it.thumb_url);
        }
      }
    }
    const items = data.items.map(it => {
      const pid = String(it.product_id);
      const url = it.thumb_url || cached.get(pid) || "";
      if (url) cached.set(pid, url);
      return { ...it, thumb_url: url };
    });
    return { ...data, items };
  };


  const key = gift_set_id;
  const isNumeric = /^\d+$/.test(key);

  const isAccessoryItem = (it) =>
  /accessor/i.test(String(it?.category || it?.product_category || "")) ||
  (it?.accessory_variant_id != null && it?.accessory_variant_id !== "");

const accessoryLabel = (it) => {
  const color = (it?.accessory_color || "").trim();
  const size  = (it?.accessory_size  || "").trim();
  if (color || size) {
    return [
      color ? `${color}` : null,
      size  ? `${size}`  : null,
    ].filter(Boolean).join(" · ");
  }
  // fallback თუ ბექი არ გასცემს color/size-ს
  return it?.accessory_variant_id ? `Variant #${it.accessory_variant_id}` : "";
};


  // 👇 ჩასვი GiftSetDetail კომპონენტის შიგნით (useState-ების შემდეგ)
const refetchGiftSet = async () => {
  setLoading(true);
  try {
    const { data } = await api.get(`${GS_BASE_URL}/gift_set_public_detail.php`, {
      params: isNumeric ? { id: key } : { gift_set_id: key }
    });
    if (data?.success) {
       const hydrated = hydrateThumbs(data);
       setGs(hydrated);
      setSelectedImage((data.images && data.images[0]) || "/placeholder-giftset.png");
    }
  } catch {}
  finally {
    setTimeout(() => setLoading(false), 150);
  }
};


// A) FULL LOAD — მხოლოდ route/key ცვლილებაზე
useEffect(() => {
  let alive = true;
  (async () => {
    setLoading(true);           // ← სპინერი მხოლოდ აქ
    try {
      const { data } = await api.get(`${GS_BASE_URL}/gift_set_public_detail.php`, {
        params: isNumeric ? { id: key } : { gift_set_id: key }
      });
      if (!alive) return;

      if (data?.success) {
        setGs(data);
        setSelectedImage((data.images && data.images[0]) || "/placeholder-giftset.png");
        setQty(1);
      } else {
        setGs(null);
      }
    } catch {
      if (alive) setGs(null);
    } finally {
      if (alive) {
        firstLoadRef.current = false;
        // ცოტა გლუვობისთვის
        setTimeout(() => setLoading(false), 150);
      }
    }
  })();

  return () => { alive = false; };
}, [key]); // ← მხოლოდ key


useEffect(() => {
  api.get(`${API_BASE_URL}/get_settings.php`)
    .then(res => {
      if (res.data?.success) {
        setSettings(res.data.settings);
      }
    })
    .catch(() => {});
}, []);
// B) SILENT REFRESH — მხოლოდ version ცვლილებაზე (სპინერის გარეშე)
useEffect(() => {
  if (firstLoadRef.current) return; // საწყისი ჩატვირთვა ზემოთ უკვე გავაკეთეთ

  let alive = true;
  (async () => {
    setSilentRefreshing(true);   // UI-ში არაფერს ვაჩვენებთ; თუ გინდა, შეგიძლია ზედა კუთხეში პატარა წერტილი/ტექსტი
    try {
      const { data } = await api.get(`${GS_BASE_URL}/gift_set_public_detail.php`, {
        params: isNumeric ? { id: key } : { gift_set_id: key }
      });
      if (!alive) return;

      if (data?.success) {
        // არანაირი სპინერი/რესტარტი — უბრალოდ განვაახლოთ მონაცემები
  setGs(prev => {
    if (Array.isArray(data.images) && data.images.length && !data.images.includes(selectedImage)) {
      setSelectedImage(data.images[0]);
    }
    // merge — items-ის thumb_url თუ ცარიელია, ძველი დაგვიტოვე
    const prevItems = prev?.items ?? [];
    const map = new Map(prevItems.map(p => [`${p.product_id}-${p.ml_id ?? "x"}`, p]));
    const nextItems = (data.items ?? []).map(n => {
      const k = `${n.product_id}-${n.ml_id ?? "x"}`;
      const old = map.get(k);
      return old ? { ...old, ...n, thumb_url: n.thumb_url || old.thumb_url || "" } : n;
    });
    return { ...(prev || {}), ...data, items: nextItems };
  });
      }
    } catch {}
    finally {
      if (alive) setSilentRefreshing(false);
    }
  })();

  return () => { alive = false; };
}, [version]); // ← მხოლოდ version

const lastSilentRefetch = useRef(0);
useEffect(() => {
  if (firstLoadRef.current) return;
  const now = Date.now();
  if (now - lastSilentRefetch.current < 800) return; // 0.8სეკ კონსოლიდაცია
  lastSilentRefetch.current = now;
  // ... silent fetch code ...
}, [version]);

  // Fallback: თუ რომელიმე item.thumb_url ცარიელია, ავავსოთ get_products.php-დან
  useEffect(() => {
    if (!gs || !Array.isArray(gs.items) || !gs.items.length) return;
    const missing = gs.items.filter(i => !i.thumb_url).map(i => String(i.product_id));
    if (!missing.length) return;

    api.get("https://shopaholics.ge/api/get_products.php")
      .then(({data}) => {
        const list = Array.isArray(data) ? data : [];
        const map = new Map(
          list.map(p => [
            String(p.product_id),
            (Array.isArray(p.imageUrls) && p.imageUrls[0]) || p.image_url || ""
          ])
        );
        setGs(prev => ({
          ...prev,
          items: prev.items.map(it => {
            const pid = String(it.product_id);
            const url = it.thumb_url || map.get(pid) || imgCacheRef.current.get(pid) || "";
            if (url) imgCacheRef.current.set(pid, url);
            return { ...it, thumb_url: url };
          })
        }));
      })
      .catch(()=>{});
  }, [gs?.id]);

  useLayoutEffect(() => {
    try { if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"; } catch {}
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const t = setTimeout(()=>window.scrollTo({top:0,left:0,behavior:"auto"}),0);
    return () => clearTimeout(t);
  }, [key]);

  if (loading) return <Loader><Spinner/></Loader>;
  if (!gs) return null;

  const title = language === "en" ? (gs.title_en || gs.title_ka) : (gs.title_ka || gs.title_en);
  const desc  = language === "en" ? (gs.description_en || gs.description_ka) : (gs.description_ka || gs.description_en);
  const note  = language === "en" ? (gs.note_en || "") : (gs.note || "");
  const method= language === "en" ? (gs.method_of_use_en || "") : (gs.method_of_use || "");
  const purp  = language === "en" ? (gs.purpose_problem_en || "") : (gs.purpose_problem || "");
  const ptype = language === "en" ? (gs.product_type_en || "") : (gs.product_type || "");
  const use   = language === "en" ? (gs.use_en || "") : (gs.use || "");

  const hasSaving = (gs.compare_price || 0) > (gs.final_price || 0);
  const percent = hasSaving ? Math.round(((gs.compare_price - gs.final_price) / (gs.compare_price || 1)) * 100) : 0;
// 🧮 Stock helpers
const stock  = Number(gs.full_stock ?? gs.stock ?? 0);
const hasStockField = gs.full_stock != null || gs.stock != null;
const isOut  = hasStockField ? stock <= 0 : false; // თუ ველი საერთოდ არაა, ღილაკს არ ვბლოკავთ
const stockText = stock > 0
  ? String(stock)
  : (language === "en" ? "Out of stock" : "არ არის მარაგში");

  const deliveryPrice = Number(settings?.delivery_fee || 0);


  const totalNow = (Number(gs.final_price||0) * (qty||0));
  const compareNow = (Number(gs.compare_price||0) * (qty||0));
  
  const totalWithDelivery = totalNow + deliveryPrice;

  const onDec = () => setQty(q => Math.max(1, q-1));
  const onInc = () => setQty(q => Math.min(99, Math.max(1, Math.min(stock || 0, q+1))));

  // 🧠 helper-ები — ზუსტად ისეთივე flow, როგორც პარფიუმში

  const isInsufficientFunds = (data = {}) => {
  const err  = String(data?.error || data?.reason || "").toLowerCase();
  const code = String(data?.error_code || data?.response_code || "").toLowerCase();
  const desc = String(data?.response_description || data?.error_message || data?.message || "").toLowerCase();

  const hay = `${err} ${code} ${desc}`;

  // ---- heuristics (works even if gateway uses different codes) ----
  return (
    err === "recurring_failed" && (
      hay.includes("insufficient") ||
      hay.includes("not enough") ||
      hay.includes("funds") ||
      hay.includes("balance") ||
      hay.includes("no money") ||
      hay.includes("51") ||        // common "insufficient funds" code in some gateways
      hay.includes("insuf") ||
      hay.includes("saldo")
    )
  ) || (
    // sometimes backend may directly send insufficient_funds
    hay.includes("insufficient_funds") ||
    hay.includes("not_enough_funds")
  );
};

const paymentFailText = (data = {}, language = "ka") => {
  if (isInsufficientFunds(data)) {
    return language === "en"
      ? "Your card balance is insufficient. Please use another card or top up and try again."
      : "ბარათზე თანხა არ არის საკმარისი. სცადეთ სხვა ბარათი ან შეავსეთ ბალანსი და ხელახლა სცადეთ.";
  }

  const msg =
    data?.response_description ||
    data?.error_message ||
    data?.error ||
    data?.reason ||
    "";

  return language === "en"
    ? (msg ? `Payment failed: ${msg}` : "Payment failed. Please try again.")
    : (msg ? `გადახდა ვერ შესრულდა: ${msg}` : "გადახდა ვერ შესრულდა. სცადეთ ხელახლა.");
};

  async function fetchMe() {
    try {
      const res = await api.get(`${API_BASE_URL}/me.php`, { withCredentials: true });
      return res.data;
    } catch {
      return { auth: false };
    }
  }
  async function openAddressPopup() {
    const me = await fetchMe();
    setPopupAssumeHasPhone(!!me?.has_phone);
    setShowAddressPopup(true);
  }

  // ✅ validate gift set
const validateGiftSet = () => {
  if (!gs) return false;

  const stock = Number(gs.full_stock || 0);
  if (stock <= 0) {
   openStockModal(T("არ არის მარაგში", "Out of stock"));
   return false;
  }

  if (!qty || qty < 1) {
   openStockModal(T("გთხოვთ მიუთითოთ სწორი რაოდენობა.", "Please select a valid quantity."));
   return false;
  }
  if (qty > stock) {
   openStockModal(T("მარაგი არასაკმარისია.", "Not enough stock for this gift set."));
   return false;
  }
  return true;
};

  // 🧾 Buy Now — stock-check → start_payment (gift_set item)
  const handleBuyNow = async (addressData) => {
    const me = await fetchMe();

    const buyerId =
      (me?.auth && (me.user?.id || me.guest_user_id || me.user_id || me.user?.user_id)) ||
      tempUser?.id;

    if (!buyerId) {
      setShowRegisterModal(true);
      return false;
    }

    // gift set item
    const gsId = gs.gift_set_id || String(gs.id);
    const unit = Number(gs.final_price || 0);
    const items = [{
      product_id: gsId,                   // ← gift_set_id as product_id
      product_type: "gift_set",
      quantity: qty,
      price: unit
    }];

    const itemsTotal = unit * qty;
    const delivery = Number(settings?.delivery_fee || 0);

    const total = itemsTotal + delivery;

    const payment_method_token = String(addressData?.payment_method_token || "");
    const save_card = Number(addressData?.save_card || 0);

    const payload = {
      user_id: buyerId,
      items,
      address: addressData?.address || "",
      city_village: addressData?.city_village || addressData?.city || "",
      comment: addressData?.comment || "",
      purchase_date: new Date().toISOString(),
      payment_method_token,
      save_card,
      lang: language
    };

    try {
      // 1) stock check
     const validationItems = [{
       product_id: gsId,
       product_type: "gift_set",
       quantity: qty,
       expected_unit_price: unit
     }];
     const stockRes = await api.post(
       `${API_BASE_URL}/check_stock.php`,
       { items: validationItems },
       { headers: { "Content-Type": "application/json" }, withCredentials: true }
     );

       // ფასის ცვლილების გამოვლენა check_stock-იდან
 if (!stockRes.data?.success) {
   if (stockRes.data?.error === "price_mismatch") {
     setShowAddressPopup(false);     // ❗
     setShowPriceModal(true);
     await refetchGiftSet();
     return false;
   }
   openStockModal(
     T(
       "მარაგი ცარიელია ან არჩეული რაოდენობა აღარ გვაქვს. გთხოვთ შეცვალოთ რაოდენობა.",
       "Out of stock or the selected quantity is no longer available. Please adjust the quantity."
     )
   );
   return false;
 }

      // 2) start payment
      const payRes = await api.post(
        `${API_BASE_URL}/start_payment.php`,
        payload,
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );

    const data = payRes?.data || {};
    const err = String(data?.error || "");


if (err === "recurring_failed" || isInsufficientFunds(data)) {
  openStockModal(paymentFailText(data, language)); // სპეციალური ტექსტით
  return false;
}

 if (!payRes.data?.success && payRes.data?.reason === "price_changed") {
   setShowAddressPopup(false);
   setShowPriceModal(true);
   await refetchGiftSet();
   return false;
 }
 if (payRes.data?.error === "stock_unavailable") {
   openStockModal(T("ბოდიში, სეტი უკვე გაიყიდა ან მარაგი აღარ არის.", "Sorry, the set was just sold or is out of stock."));
   return false;
 }

      if (payRes.data?.success && payRes.data?.redirect_url) {
        setShowAddressPopup(false);
        setPayUrl(payRes.data.redirect_url);
        setPayModalOpen(true);
        return true;
      }

      if (payRes.data?.success) {
        alert(language === "en" ? "✅ Purchase successful!" : "✅ შეძენა წარმატებულია!");
        return true;
      }

      if (payRes.data?.error === "Unauthorized request!") {
        setShowAuthModal(true);
        return false;
      }

      alert(
        language === "en"
          ? `❌ Error: ${payRes.data?.error || "Unknown"}`
          : `❌ შეცდომა: ${payRes.data?.error || "უცნობი"}`
      );
      return false;
    } catch (e) {
      console.error(e);
      alert(
        language === "en"
          ? "❌ An error occurred while processing your purchase."
          : "❌ შესყიდვის პროცესში წარმოიშვა შეცდომა."
      );
      return false;
    }
  };

  const onAddSetToCart = () => {
    if (typeof addToCart === 'function') {
      addToCart({
        type: 'gift_set',
        gift_set_id: gs.gift_set_id || String(gs.id),
        product_id:  gs.gift_set_id || String(gs.id),
        id:          gs.gift_set_id || String(gs.id),
        title,
        name: title,
        unit_price: Number(gs.final_price || 0),
        quantity: qty,
        full_stock: Number(gs.full_stock || 0),
        image: (gs.images && gs.images[0]) || "",
      });
      return;
    }
    try {
      const key = 'cart';
      const raw = localStorage.getItem(key);
      const cart = raw ? JSON.parse(raw) : [];
      cart.push({
        type: 'gift_set',
        id: gs.id,
        slug: gs.slug,
        title,
        unit_price: Number(gs.final_price || 0),
        qty,
        cover: (gs.images && gs.images[0]) || "",
      });
      localStorage.setItem(key, JSON.stringify(cart));
      alert(language==="en" ? "Gift set added to cart" : "გიფტ სეტი დაემატა კალათაში");
    } catch {}
  };

  // 🔘 Buy Now ღილაკი — იგივე UX-ით, რაც PerfumeDetail-ში
  const onBuySetNow = async () => {
    if (!validateGiftSet()) return;
    const stored = localStorage.getItem("user");
    const u = stored ? JSON.parse(stored) : null;
    if (!u?.id) {
      setShowRegisterModal(true);
    } else {
      await openAddressPopup();
    }
  };

  return (
    <>
      <Page>
        <TitleMobile>{title}</TitleMobile>

        <Grid>
          {/* LEFT: Gallery */}
          <GalleryCard>
            <MainImageWrap>
              <MainImage src={selectedImage} alt={title} />
            </MainImageWrap>
            <Thumbs>
              {(gs.images||[]).map((u)=>(
                <img key={u} src={u} alt={`${title} ${u+1}`} className={selectedImage===u?'selected':''} onClick={()=>setSelectedImage(u)} />
              ))}
            </Thumbs>
          </GalleryCard>

          {/* RIGHT: Details + Actions */}
          <div>
            <DetailsCard>
              <TitleDesktop>{title}</TitleDesktop>

              <PriceRow>
                {hasSaving && <span className="old">{Number(gs.compare_price).toFixed(2)} ₾</span>}
                <span>{Number(gs.final_price||0).toFixed(2)} ₾</span>
                {hasSaving && <span style={{marginLeft:6, color:'#059669', fontWeight:900}}>−{percent}%</span>}
              </PriceRow>

              {gs.brand && <Row><strong>{t("detailed.brand")}:</strong><span>{gs.brand}</span></Row>}
              {gs.sex && <Row><strong>{t("detailed.sex")}:</strong>
              <span>
                    {gs.sex==="woman" ? t("category.woman")
                    : gs.sex==="man" ? t("category.man")
                    : gs.sex==="unisex" ? t("category.unisex")
                    : gs.sex==="kids" ? t("category.kids")
                    : gs.sex}
                </span></Row>}
                {gs.category && <Row><strong>{t("detailed.category")}:</strong>
                <span>
                  {gs.category==="perfume" ? t("category.perfume")
                    : gs.category==="skin-care" ? t("category.skinCare")
                    : gs.category==="hair-and-body-care" ? t("category.hairAndBodyCare")
                    : gs.category==="accessories" ? t("category.accessories")
                    : gs.category}
                </span>
              </Row>}
              <Row>
              <strong>{t("detailed.full_stock")}:</strong>
              <span>{stockText}</span>
            </Row>
              {ptype && <Info><strong>{t("detailed.producttype")}:</strong> {ptype}</Info>}
              {use && <Info><strong>{t("detailed.use")}:</strong> {use}</Info>}
              {method && <Info><strong>{t("detailed.methodofuse")}:</strong> {method}</Info>}
              {purp && <Info><strong>{t("detailed.purposeproblem")}:</strong> {purp}</Info>}

              {note && (
                <>
                  <Toggle onClick={()=>setShowNote(o=>!o)} aria-expanded={showNote}>
                    <span><strong>Note</strong></span>
                    <span>{showNote ? "−" : "＋"}</span>
                  </Toggle>
                  <ToggleContent open={showNote}>
                    <p>{note}</p>
                  </ToggleContent>
                </>
              )}
              {desc && (
                <>
                  <Toggle onClick={()=>setShowDesc(o=>!o)} aria-expanded={showDesc}>
                    <span><strong>{t("detailed.description")}</strong></span>
                    <span>{showDesc ? "−" : "＋"}</span>
                  </Toggle>
                  <ToggleContent open={showDesc}>
                    <p>{desc}</p>
                  </ToggleContent>
                </>
              )}
            </DetailsCard>

            {/* Actions (qty + total + buttons) */}
            <ActionsCard>
              <QtyRow>
                <QtyBadge>{t("detailed.quantity")}:</QtyBadge>
                <QtyField>
                  <QtyBtn onClick={onDec}>−</QtyBtn>
                  <QtyInput type="number" readOnly value={qty} />
                  <QtyBtn onClick={onInc}>+</QtyBtn>
                </QtyField>
              </QtyRow>

<div
  style={{
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
    display: "grid",
    gap: 6,
    marginTop: 12,
  }}
>
  {/* Products total */}
  <div style={{ display: "flex", justifyContent: "space-between" }}>
    <span style={{ fontWeight: 800 }}>
      {language === "ka" ? "პროდუქტების ჯამი" : "Products total"}
    </span>
    <span style={{ fontWeight: 900 }}>
      {totalNow.toFixed(2)} ₾
    </span>
  </div>

  {/* Delivery */}
  <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>
    <span style={{ fontWeight: 700 }}>
      {language === "ka" ? "მიწოდება" : "Delivery"}
    </span>
    <span style={{ fontWeight: 800 }}>
      {deliveryPrice.toFixed(2)} ₾
    </span>
  </div>

  <hr style={{ border: "none", borderTop: "1px solid rgba(0,0,0,.08)" }} />

  {/* Total */}
  <div style={{ display: "flex", justifyContent: "space-between" }}>
    <span style={{ fontWeight: 900 }}>
      {language === "ka" ? "სრული ფასი" : "Total price"}
    </span>
    <span style={{ fontWeight: 900 }}>
      {(totalNow + deliveryPrice).toFixed(2)} ₾
    </span>
  </div>
</div>
            <Buttons>
              <button className="btn primary" onClick={onBuySetNow} disabled={isOut}>
                {t("detailed.buynow")}
              </button>
              <div className="or">ან</div>
              <button className="btn secondary" onClick={onAddSetToCart} disabled={isOut}>
                {t("detailed.addtocart")}
              </button>
            </Buttons>
            </ActionsCard>

            {/* Items list */}
            <ItemsCard>
              <h3 style={{margin:'12px 0 10px', fontWeight:900}}>{t("detailed.include_set")}</h3>
              {(gs.items||[]).map((it, idx) => {
                const itemKey = `${it.product_id}-${it.ml_id ?? "x"}`;
                const mlLabel = it.ml_size != null ? `${it.ml_size} ml` : (it.ml_id ? `#${it.ml_id}` : "—");
                const cover = it.thumb_url || "/placeholder.jpg";
                const onThumbError = (e) => { e.target.onerror = null; e.target.src = "/placeholder.jpg"; };
                return (
                  <ItemRow key={itemKey}>
                    <Thumb><img src={cover} alt="" onError={onThumbError} loading="lazy" decoding="async" /></Thumb>
                    <div style={{minWidth:0}}>
                      <div style={{display:'flex', gap:8, alignItems:'baseline', flexWrap:'wrap'}}>
                        <div style={{fontWeight:900, color:COLORS.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                          {it.name || it.product_id}
                        </div>
                        {it.brand && <div style={{color:COLORS.dim}}>· {it.brand}</div>}
                      </div>
<div style={{display:'flex', gap:10, flexWrap:'wrap', marginTop:4, color:COLORS.dim}}>
  {/* აქსესუარი → ფერი/ზომა; თორემ ML */}
  {isAccessoryItem(it) ? (
    accessoryLabel(it) && (
      <div>
        {language === "en" ? "Variant" : "ვარიანტი"}:{" "}
        <b style={{color:COLORS.text}}>{accessoryLabel(it)}</b>
      </div>
    )
  ) : (
    mlLabel !== "—" && (
      <div>
        ML: <b style={{color:COLORS.text}}>{mlLabel}</b>
      </div>
    )
  )}

  <div>
    Qty: <b style={{color:COLORS.text}}>{it.quantity}</b>
  </div>
  <div>
    Unit: <b style={{color:COLORS.text}}>{Number(it.unit_price).toFixed(2)} ₾</b>
  </div>

  {it.override_price != null && (
    <div style={{color:'#059669', fontWeight:800}}>override</div>
  )}
</div>
                    </div>
                    <div style={{display:'flex', justifyContent:'flex-end'}}>
                      <QuickBtn onClick={()=>setOpenQuick(it.product_id)}>{t("detailed.quick_view")}</QuickBtn>
                    </div>
                  </ItemRow>
                );
              })}
            </ItemsCard>
          </div>
        </Grid>
      </Page>

      {/* Quick View = PerfumeDetail in modal */}
      {openQuick && (
        <ModalBg onClick={() => setOpenQuick(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <CloseBtn
              type="button"
              aria-label={language === "en" ? "Close" : "დახურვა"}
              onClick={() => setOpenQuick(null)}
              title={language === "en" ? "Close" : "დახურვა"}
            >
              ✕
            </CloseBtn>
            <ModalScroll>
              <PerfumeDetail forceProductId={openQuick} hideActions />
            </ModalScroll>
          </ModalCard>
        </ModalBg>
      )}

      {/* 🔻 იგივე მოდალები, რაც PerfumeDetail-ში */}
      <PriceChangedModal
        show={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        language={language}
      />

      <AuthExpiredModal
        visible={showAuthModal}
        language={language}
        onClose={() => {
          setShowAuthModal(false);
          localStorage.removeItem("user");
          window.location.href = "/login";
        }}
      />

      {stockModalOpen && (
        <StockModal message={stockMessage} onClose={() => setStockModalOpen(false)} />
      )}

{payModalOpen && (
  <PaymentIframeModal
    url={payUrl}
    language={language}
    onClose={() => {
      setPayModalOpen(false);
      setPayUrl("");
    }}
  />
)}


      {showRegisterModal && (
        <MiniRegisterModal
          onClose={() => setShowRegisterModal(false)}
          onSubmitted={async (payload) => {
            try {
              const res = await api.post(
                `${API_BASE_URL}/guest_start.php`,
                { ...payload, lang: language },
                { headers: { "Content-Type": "application/json" }, withCredentials: true }
              );
              const data = res.data;
              if (data?.success) {
                setTempUser({
                  id: data.guest_user_id || data.user_id || data?.user?.id,
                  type: "guest",
                  email: payload?.email,
                });
                setShowRegisterModal(false);
                const guessedHasPhone = !!payload?.phone || !!payload?.phone_e164;
                setPopupAssumeHasPhone(guessedHasPhone);
                setShowAddressPopup(true);
              }
              return data;
            } catch (e) {
              console.error(e);
              return { success: false, error_code: "NETWORK", error: "Network error" };
            }
          }}
        />
      )}

      {showAddressPopup && (
        <AddressPopup
          onClose={() => setShowAddressPopup(false)}
          onSubmit={async (data) => {
            const ok = await handleBuyNow(data);
            if (ok) setShowAddressPopup(false);
          }}
          assumeHasPhone={popupAssumeHasPhone}
          knownUserId={ tempUser?.id || null }
        />
      )}
    </>
  );
}
